import type { Lead, LeadConvert, LeadCreate, LeadStatus, LeadUpdate } from '@erp/shared';
import { Pencil, Trash2, UserCheck, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

const LEAD_STATUSES: LeadStatus[] = ['new', 'contacted', 'qualified', 'converted', 'lost'];

const STATUS_VARIANT: Record<
  LeadStatus,
  'info' | 'success' | 'warning' | 'danger' | 'secondary'
> = {
  new: 'info',
  contacted: 'warning',
  qualified: 'success',
  converted: 'success',
  lost: 'secondary',
};

export function LeadsPage(): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('crm.leads.write');
  const [items, setItems] = useState<Lead[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Lead | null>(null);
  const [convertTarget, setConvertTarget] = useState<Lead | null>(null);

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: Lead[] }>('/crm/leads');
    setItems(r.data.items);
  };

  useEffect(() => {
    void reload();
  }, []);

  if (!items) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const openEdit = (l: Lead): void => {
    setShowCreate(false);
    setEditing(l);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDelete = async (l: Lead): Promise<void> => {
    if (!(await confirmDialog({ message: t('leads.actions.confirmDelete', { name: l.displayName }), tone: 'danger' }))) return;
    try {
      await api().delete(`/crm/leads/${l.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const columns: DataTableColumn<Lead>[] = [
    {
      id: 'name',
      header: t('leads.columns.name'),
      sortValue: (l) => l.displayName,
      searchValue: (l) => l.displayName,
      cellClassName: 'font-medium',
      cell: (l) => l.displayName,
    },
    {
      id: 'company',
      header: t('leads.columns.company'),
      sortValue: (l) => l.company ?? '',
      searchValue: (l) => l.company ?? '',
      cellClassName: 'text-fg-muted',
      cell: (l) => l.company ?? '—',
    },
    {
      id: 'email',
      header: t('leads.columns.email'),
      sortValue: (l) => l.email ?? '',
      searchValue: (l) => l.email ?? '',
      cellClassName: 'text-fg-muted',
      cell: (l) => l.email ?? '—',
    },
    {
      id: 'source',
      header: t('leads.columns.source'),
      sortValue: (l) => l.source ?? '',
      searchValue: (l) => l.source ?? '',
      cellClassName: 'font-mono text-xs',
      cell: (l) => l.source ?? '—',
    },
    {
      id: 'status',
      header: t('leads.columns.status'),
      sortValue: (l) => t(`leads.status.${l.status}`),
      searchValue: (l) => t(`leads.status.${l.status}`),
      cell: (l) => <Badge variant={STATUS_VARIANT[l.status]}>{t(`leads.status.${l.status}`)}</Badge>,
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-32',
            cell: (l: Lead) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(l)}
                  aria-label={`${t('leads.actions.edit')} ${l.displayName}`}
                  title={t('leads.actions.edit')}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                {l.status !== 'converted' && l.status !== 'lost' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConvertTarget(l)}
                    title={t('leads.convert.button')}
                  >
                    <UserCheck className="h-4 w-4" aria-hidden />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(l)}
                  aria-label={`${t('leads.actions.delete')} ${l.displayName}`}
                  title={t('leads.actions.delete')}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('leads.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('leads.description')}</p>
        </div>
        {canWrite && !showCreate && !editing && (
          <Button onClick={() => setShowCreate(true)}>{t('leads.create.title')}</Button>
        )}
      </header>

      {showCreate && (
        <CreateLeadForm
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <EditLeadForm
          lead={editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}

      {items.length === 0 && !showCreate ? (
        <EmptyState
          icon={<UserPlus className="h-8 w-8" aria-hidden />}
          title={t('leads.empty')}
          description={t('leads.emptyDescription')}
          action={
            canWrite ? (
              <Button onClick={() => setShowCreate(true)}>{t('leads.create.title')}</Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={items}
            rowKey={(l) => l.id}
            searchPlaceholder={t('leads.searchPlaceholder')}
          />
        </Card>
      )}

      {convertTarget && (
        <ConvertLeadDialog
          lead={convertTarget}
          onClose={() => setConvertTarget(null)}
          onConverted={async () => {
            setConvertTarget(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}

interface CreateFormProps { onCancel: () => void; onCreated: () => void | Promise<void>; }

function CreateLeadForm({ onCancel, onCreated }: CreateFormProps): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  const [form, setForm] = useState<LeadCreate>({ firstName: '', lastName: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api().post('/crm/leads', form);
      await onCreated();
    } catch {
      setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">{t('leads.create.title')}</h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="l-first" label={t('leads.create.firstName')} required>
          <Input id="l-first" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required autoFocus />
        </FormField>
        <FormField id="l-last" label={t('leads.create.lastName')} required>
          <Input id="l-last" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
        </FormField>
        <FormField id="l-company" label={t('leads.create.company')}>
          <Input id="l-company" value={form.company ?? ''} onChange={(e) => setForm({ ...form, company: e.target.value })} />
        </FormField>
        <FormField id="l-email" label={t('leads.create.email')}>
          <Input id="l-email" type="email" value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </FormField>
        <FormField id="l-source" label={t('leads.create.source')}>
          <Input id="l-source" value={form.source ?? ''} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="web, referral, event, …" />
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{tc('actions.create')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface ConvertDialogProps {
  lead: Lead;
  onClose: () => void;
  onConverted: () => void | Promise<void>;
}

function ConvertLeadDialog({ lead, onClose, onConverted }: ConvertDialogProps): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  const [form, setForm] = useState<LeadConvert>({
    accountName: lead.company ?? lead.displayName,
    opportunityName: `${lead.company ?? lead.displayName} — Opportunity`,
    currencyCode: 'USD',
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: LeadConvert = { ...form };
      // Strip empty optional fields so the API doesn't reject empty strings.
      if (!payload.amount) delete payload.amount;
      if (!payload.expectedCloseDate) delete payload.expectedCloseDate;
      if (!payload.accountName) delete payload.accountName;
      await api().post(`/crm/leads/${lead.id}/convert`, payload);
      await onConverted();
    } catch (err) {
      setError((err as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-lg border border-border bg-bg p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-4">
          <h2 className="text-lg font-semibold">{t('leads.convert.title')}</h2>
          <p className="mt-1 text-sm text-fg-muted">
            {t('leads.convert.subtitle', { name: lead.displayName })}
          </p>
        </header>
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
          <div className="md:col-span-2">
            <FormField id="cv-account-name" label={t('leads.convert.accountName')}>
              <Input
                id="cv-account-name"
                value={form.accountName ?? ''}
                onChange={(e) => setForm({ ...form, accountName: e.target.value })}
              />
            </FormField>
          </div>
          <div className="md:col-span-2">
            <FormField id="cv-opp-name" label={t('leads.convert.opportunityName')} required>
              <Input
                id="cv-opp-name"
                value={form.opportunityName}
                onChange={(e) => setForm({ ...form, opportunityName: e.target.value })}
                required
                autoFocus
              />
            </FormField>
          </div>
          <FormField id="cv-amount" label={t('leads.convert.amount')}>
            <Input
              id="cv-amount"
              inputMode="decimal"
              value={form.amount ?? ''}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="0.0000"
            />
          </FormField>
          <FormField id="cv-currency" label={t('leads.convert.currency')}>
            <CurrencySelect
              id="cv-currency"
              value={form.currencyCode ?? ''}
              onChange={(c) => setForm({ ...form, currencyCode: c })}
              includeEmpty
            />
          </FormField>
          <div className="md:col-span-2">
            <FormField id="cv-close" label={t('leads.convert.expectedClose')}>
              <Input
                id="cv-close"
                type="date"
                value={form.expectedCloseDate ?? ''}
                onChange={(e) => setForm({ ...form, expectedCloseDate: e.target.value })}
              />
            </FormField>
          </div>
          {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
          <div className="md:col-span-2 flex gap-2">
            <Button type="submit" loading={saving}>{t('leads.convert.submit')}</Button>
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              {tc('actions.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface EditLeadFormProps {
  lead: Lead;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function EditLeadForm({ lead, onCancel, onSaved }: EditLeadFormProps): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  // Mirror the create form fields plus status. Notes / phone / ownerUserId
  // are deliberately not surfaced here — the create form doesn't include
  // them either, so adding them on the edit side only would be confusing.
  // The status field is edit-only by design: status flows from `new` via
  // operator action; on create everything starts at `new`.
  const [form, setForm] = useState<LeadUpdate>({
    firstName: lead.firstName,
    lastName: lead.lastName,
    company: lead.company,
    email: lead.email,
    source: lead.source,
    status: lead.status,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const patch: LeadUpdate = {
        firstName: form.firstName?.trim() || undefined,
        lastName: form.lastName?.trim() || undefined,
        // Empty optional text fields collapse to null so the server clears them.
        company: form.company && form.company.trim() ? form.company.trim() : null,
        email: form.email && form.email.trim() ? form.email.trim() : null,
        source: form.source && form.source.trim() ? form.source.trim() : null,
        status: form.status,
      };
      await api().patch(`/crm/leads/${lead.id}`, patch);
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'unprocessable' ? t('leads.errors.unprocessable') : tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {t('leads.edit.title', { name: lead.displayName })}
      </h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="le-first" label={t('leads.create.firstName')} required>
          <Input
            id="le-first"
            value={form.firstName ?? ''}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            required
            autoFocus
          />
        </FormField>
        <FormField id="le-last" label={t('leads.create.lastName')} required>
          <Input
            id="le-last"
            value={form.lastName ?? ''}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            required
          />
        </FormField>
        <FormField id="le-company" label={t('leads.create.company')}>
          <Input
            id="le-company"
            value={form.company ?? ''}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          />
        </FormField>
        <FormField id="le-email" label={t('leads.create.email')}>
          <Input
            id="le-email"
            type="email"
            value={form.email ?? ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </FormField>
        <FormField id="le-source" label={t('leads.create.source')}>
          <Input
            id="le-source"
            value={form.source ?? ''}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
          />
        </FormField>
        <FormField id="le-status" label={t('leads.edit.status')}>
          <select
            id="le-status"
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.status ?? 'new'}
            onChange={(e) => setForm({ ...form, status: e.target.value as LeadStatus })}
          >
            {LEAD_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`leads.status.${s}`)}
              </option>
            ))}
          </select>
        </FormField>
        {error && (
          <p className="md:col-span-2 text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>
            {t('leads.edit.submit')}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {t('leads.edit.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
